import * as React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import { useGetStaffQuery } from "services/staff";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { selectHasRegions } from "features/auth/auth-slice";
import { MinimalStaffMember, StaffMember } from "services/types/staff.type";
import {
  selectClinicsFilterValues,
  selectSelectedFromAvailableStaff,
  setSelectedFromAvailableStaff,
} from "../../scheduler-slice";
import { useCallback, useEffect } from "react";
import { intersectionBy, isEqual } from "lodash";

type AvailableStaffFilterProps = {
  showFilter: boolean;
};
export default function AvailableStaffFilter({
  showFilter,
}: AvailableStaffFilterProps) {
  const dispatch = useAppDispatch();
  const selectedStaff = useAppSelector(selectSelectedFromAvailableStaff);
  const hasRegions = useAppSelector(selectHasRegions);
  const clinicsFilterIds = useAppSelector(selectClinicsFilterValues);

  const setAvailableStaff = useCallback(
    (value: (MinimalStaffMember | StaffMember)[]): any =>
      dispatch(setSelectedFromAvailableStaff(value)),
    [dispatch]
  );
  const { data: availableStaff = [], isSuccess } = useGetStaffQuery({
    clinicIds: hasRegions ? clinicsFilterIds : undefined,
  });

  useEffect(() => {
    if (isSuccess) {
      const calculatedStaff = intersectionBy(
        intersectionBy(
          selectedStaff,
          availableStaff,
          "id"
        ) as MinimalStaffMember[]
      );
      if (!isEqual(calculatedStaff, selectedStaff)) {
        setAvailableStaff(calculatedStaff);
      }
    }
  }, [selectedStaff, availableStaff, isSuccess, setAvailableStaff]);

  if (!showFilter) {
    return null;
  }

  return (
    <Autocomplete
      multiple
      options={availableStaff as MinimalStaffMember[]}
      limitTags={2}
      getOptionLabel={(option) => option.fullName}
      onChange={(_event, value) => {
        setAvailableStaff(value);
      }}
      renderTags={(tagValue, getTagProps) => {
        return [...tagValue].map((option, index) => (
          <Chip label={option?.fullName} {...getTagProps({ index })} />
        ));
      }}
      filterSelectedOptions
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderInput={(params) => (
        <TextField
          data-testid="availability-staff-search"
          {...params}
          placeholder="Select staff member to see their availability"
        />
      )}
      value={selectedStaff}
    />
  );
}
